/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import gatewayFetch from "utilities/gatewayFetch";
import { directUpload, S3Folders } from "utilities/ProjectUtilities";
import { NewRelicDecoratedError, noticeError } from "utilities/noticeError";
import { mapFromServerFormat, mapToServerFormat } from "utilities/ApiUtilities";
import { AssetSet } from "types/Asset";
import { ClipWithPlaylistTimes } from "types/Clip";
import Analytics, { AnalyticsEvent } from "constants/Analytics";
import { DescribedEnhanceSpeechLimits } from "components/EnhancedSpeech/useEnhanceSpeechLimits";
import {
  EnhancementObject,
  ProcessingState,
} from "components/EnhancedSpeech/useEnhanceSpeech/EnhancementObject";
import {
  CompletedEnhanceSpeechTrack,
  EnhanceSpeechTrack,
  EnhanceSpeechTrackErrorType,
  EnhanceSpeechTrackId,
} from "components/EnhancedSpeech/EnhanceSpeechTrack";
import { computeTwoSliderGains } from "components/EnhancedSpeech/computeTwoSliderGains";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";

export * from "components/EnhancedSpeech/EnhanceSpeechTrack";

export const TOO_MANY_REQUESTS_STATUS = 429;
export const PAYLOAD_TOO_LARGE_STATUS = 413;
export const DAILY_DURATION_LIMIT_STATUS = 422;
export const TOO_MANY_IN_QUEUE_STATUS = 409;
export const NOT_FOUND_STATUS = 404;
export const UNSUPPORTED_EXTENSION_OR_CONTENT_TYPE = 415;

export const fetchWithDebug = async (
  url: string,
  init?: RequestInit
): Promise<Response> => {
  const startTimeMs = new Date().getTime();
  let wentOffline = false;
  const offlineListener = (): void => {
    wentOffline = true;
  };
  window.addEventListener("offline", offlineListener);
  try {
    const response = await fetch(url, init);
    return response;
  } catch (error) {
    const endTimeMs = new Date().getTime();
    const durationMs = endTimeMs - startTimeMs;
    const urlToReport = url ? new URL(url).origin : "s3-download";
    newrelic.noticeError(error, {
      url: urlToReport,
      method: "GET",
      navigatorOnLine: String(navigator.onLine),
      isSignedInUser: String(window.adobeIMS?.isSignedInUser()),
      wentOffline: String(wentOffline),
      durationMs,
    });
    error.alreadyReportedToNewRelic = true;
    throw error;
  } finally {
    window.removeEventListener("offline", offlineListener);
  }
};

export class EnhanceSpeechUploadException extends NewRelicDecoratedError {
  constructor(errorOrString: Error | string) {
    super(errorOrString);
    this.name = "EnhanceSpeechUploadException";
  }
}

export class EnhanceSpeechPollingException extends NewRelicDecoratedError {
  constructor(message: string) {
    super("EnhanceSpeechPollingException", {
      expected: String(true),
    });
    this.name = "EnhanceSpeechPollingException";
    this.message = message;
  }
}

export const fetchExpiringUrl = async (url: string): Promise<string> => {
  // This is only to support the sample assets player that do not have download URLs
  if (url.startsWith(`${process.env.CDN_ROOT}`)) {
    return url;
  }

  const presignedUrlResponse = await gatewayFetch(
    url,
    {
      method: "GET",
    },
    { reportErrors: false }
  );

  if (!presignedUrlResponse.ok) {
    noticeError("Unable to get presigned URL", {
      url,
      statusCode: presignedUrlResponse.status,
    });

    // TODO: We should use exceptions instead of returning "error" values
    return "";
  }

  const presignedUrlJson = await presignedUrlResponse.json();

  return presignedUrlJson.url;
};

export const isEnhanceSpeechCompletedSuccessfully = (
  track: EnhanceSpeechTrack | undefined
): track is CompletedEnhanceSpeechTrack => {
  return (
    track !== undefined &&
    Boolean(track.finishedProcessingAt) &&
    !Boolean(track.errorType)
  );
};

export const buildExportTrackName = (track: EnhanceSpeechTrack): string => {
  return (
    track.trackName.split(".").slice(0, -1).join(".") +
    `-${I18n.t("enhance_speech.export.file_postfix")}-${track.modelVersion}.wav`
  );
};

export const deleteEnhanceSpeechTrack = async (
  id: EnhanceSpeechTrackId
): Promise<boolean> => {
  try {
    const response = await gatewayFetch(
      `/api/v1/enhance_speech_tracks/${id}`,
      {
        method: "DELETE",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
      { reportErrors: false }
    );

    if (!response.ok) {
      console.error(response);
      noticeError("Unexpected response deleting enhance speech track", {
        status: response.status,
      });
    }
    return response.ok;
  } catch (error) {
    console.error(error);
    noticeError(error);
    return false;
  }
};

export const findNextCompletedEnhanceSpeechTrackId = (
  targetTrackId: EnhanceSpeechTrackId,
  enhancements: EnhancementObject[]
): EnhanceSpeechTrackId | undefined => {
  // Iterates over the enhancement list and keeps track of the first completed
  // enhancement seen before and after the target enhancement
  let targetEnhancementSeen = false;
  let preTargetNextCompletedEnhancement:
    | CompletedEnhanceSpeechTrack
    | undefined;
  let postTargetNextCompletedEnhancement:
    | CompletedEnhanceSpeechTrack
    | undefined;

  enhancements.forEach((enhancement) => {
    if (enhancement.track.id == targetTrackId) {
      targetEnhancementSeen = true;
      return;
    }

    if (!targetEnhancementSeen) {
      if (
        !preTargetNextCompletedEnhancement &&
        isEnhanceSpeechCompletedSuccessfully(enhancement.track)
      ) {
        preTargetNextCompletedEnhancement = enhancement.track;
      }
    } else {
      if (
        !postTargetNextCompletedEnhancement &&
        isEnhanceSpeechCompletedSuccessfully(enhancement.track)
      ) {
        postTargetNextCompletedEnhancement = enhancement.track;
      }
    }
  });

  // Prefer the first completed enhancement AFTER the target enhancement, but if there
  // is none, then fall back to the first completed enhancement in the entire list.
  const nextCompletedEnhancement =
    postTargetNextCompletedEnhancement || preTargetNextCompletedEnhancement;

  return nextCompletedEnhancement?.id;
};

export const fetchEnhanceSpeechTracks = async (): Promise<
  EnhanceSpeechTrack[]
> => {
  const response = await gatewayFetch(
    `/api/v1/enhance_speech_tracks`,
    {
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
    {
      reportErrors: false,
    }
  );

  if (!response.ok) {
    newrelic.noticeError("Unexpected response fetching enhance speech tracks", {
      status: response.status,
    });
    return [];
  }

  const json = (await response.json()) as unknown[];
  return json.map(mapFromServerFormat) as EnhanceSpeechTrack[];
};

export type DirectUploadSignedId = string;

export const uploadFile = async (
  file: File,
  progressCallback?: (percentComplete: number) => void
): Promise<DirectUploadSignedId> => {
  let uploadResponse;

  try {
    uploadResponse = await directUpload({
      fileOrBlob: file,
      folderPath: S3Folders.QuickActions,
      progressCallback,
    });
  } catch (error) {
    Analytics.instance.logEvent(AnalyticsEvent.ENHANCE_UPLOAD_FAILED, {
      file_type: file.type,
      file_size: file.size,
    });

    throw new EnhanceSpeechUploadException(error);
  }
  return uploadResponse.signed_id;
};

export const enhanceFile = async (
  file: File,
  directUploadSignedId: DirectUploadSignedId,
  newTrack: EnhanceSpeechTrack
): Promise<EnhanceSpeechTrack> => {
  const createResponse = await gatewayFetch(
    `/api/v1/enhance_speech_tracks`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        id: newTrack.id,
        track_name: file.name,
        model_version: newTrack.modelVersion,
        signed_id: directUploadSignedId,
      }),
    },
    { reportErrors: false }
  );

  if (!createResponse.ok) {
    const retryAfterInSeconds = createResponse.headers.get("Retry-After");
    const now = new Date();
    const retryAtDate = retryAfterInSeconds
      ? new Date(now.getTime() + Number(retryAfterInSeconds) * 1000)
      : now;

    if (createResponse.status == TOO_MANY_REQUESTS_STATUS) {
      return {
        ...newTrack,
        errorType: EnhanceSpeechTrackErrorType.RATE_LIMIT,
        limits: {
          isLimited: true,
          retryAtTime: retryAtDate.toISOString(),
        },
      };
    } else if (createResponse.status == PAYLOAD_TOO_LARGE_STATUS) {
      return {
        ...newTrack,
        errorType: EnhanceSpeechTrackErrorType.FILE_SIZE_LIMIT,
      };
    } else if (createResponse.status == DAILY_DURATION_LIMIT_STATUS) {
      return {
        ...newTrack,
        errorType: EnhanceSpeechTrackErrorType.CUMULATIVE_DURATION_LIMIT,
        limits: {
          isLimited: true,
          retryAtTime: retryAtDate.toISOString(),
        },
      };
    } else if (createResponse.status == TOO_MANY_IN_QUEUE_STATUS) {
      return {
        ...newTrack,
        errorType: EnhanceSpeechTrackErrorType.CONCURRENCY_LIMIT,
      };
    } else if (createResponse.status == UNSUPPORTED_EXTENSION_OR_CONTENT_TYPE) {
      return {
        ...newTrack,
        errorType: EnhanceSpeechTrackErrorType.UNSUPPORTED_EXTENSION,
      };
    } else {
      throw Error(
        `Creating enhance speech track failed with status: ${createResponse.status}`
      );
    }
  }

  const track: EnhanceSpeechTrack = mapFromServerFormat(
    await createResponse.json()
  ) as EnhanceSpeechTrack;

  return track;
};

export type EnhanceSpeechTrackExportId = string;

// see EnhanceSpeechTrackExport::as_json
export interface EnhanceSpeechTrackExport {
  id: EnhanceSpeechTrackExportId;
  enhanceSpeechTrackId: EnhanceSpeechTrackId;
  url: string | null;
  errorType: string | null;
  finishedProcessingAt: string | null;
  startedProcessingAt: string | null;
  createdAt: string;
}

export const createEnhanceSpeechTrackExport = async (
  trackId: EnhanceSpeechTrackId,
  enhanceStrengthPercent: number,
  backgroundPercent: number,
  enhancementToggleEnabled: boolean,
  modelVersion: string
): Promise<EnhanceSpeechTrackExport> => {
  let body: {};

  const bypassMixing = !enhancementToggleEnabled;

  if (modelVersion === ModelVersion.v2) {
    const enhanceGains = computeTwoSliderGains(
      enhanceStrengthPercent,
      backgroundPercent,
      bypassMixing,
      modelVersion
    );

    body = {
      enhancedSpeechGain: enhanceGains.enhancedSpeechGain,
      backgroundGain: enhanceGains.backgroundGain,
      enhancementEnabled: enhancementToggleEnabled,
    };
  } else {
    body = {
      strength: enhanceStrengthPercent,
      enhancementEnabled: enhancementToggleEnabled,
    };
  }

  const response = await gatewayFetch(
    `/api/v1/enhance_speech_tracks/${trackId}/exports`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(mapToServerFormat(body)),
    }
  );

  return mapFromServerFormat(await response.json()) as EnhanceSpeechTrackExport;
};

export const getEnhanceSpeechTrackExport = async (
  trackId: EnhanceSpeechTrackId,
  exportId: EnhanceSpeechTrackExportId
): Promise<EnhanceSpeechTrackExport> => {
  const response = await gatewayFetch(
    `/api/v1/enhance_speech_tracks/${trackId}/exports/${exportId}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    }
  );

  return mapFromServerFormat(await response.json()) as EnhanceSpeechTrackExport;
};

async function gatewayFetchWithMappedException(
  url: string,
  options: RequestInit
): Promise<Response> {
  try {
    return await gatewayFetch(url, options, { reportErrors: false });
  } catch (error) {
    throw new EnhanceSpeechPollingException(error.message);
  }
}

export const fetchTrackStatus = async (trackId: string): Promise<Response> => {
  return await gatewayFetchWithMappedException(
    `/api/v1/enhance_speech_tracks/${trackId}`,
    {
      method: "GET",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }
  );
};

export interface EnhanceSpeechTrackPlayback {
  getPlaylist(enhanceStrengthPercent: number): ClipWithPlaylistTimes[];
  assets: AssetSet;
}

// todo: const filename = track.trackName.split(".").slice(0, -1).join(".") + " (enhanced).wav";

export const userMessageForEnhanceTrackError = (
  errorType: EnhanceSpeechTrackErrorType,
  userLimits: DescribedEnhanceSpeechLimits,
  retryAtIntervalDescription: string | null
): string => {
  switch (errorType) {
    case EnhanceSpeechTrackErrorType.DEPRECATED_SENSEI_TIMEOUT_EXCEPTION:
    case EnhanceSpeechTrackErrorType.PROCESSING_TIMEOUT:
      return I18n.t("enhance_speech.enhance_timed_out");
    case EnhanceSpeechTrackErrorType.DEPRECATED_SENSEI_UNSUPPORTED_FORMAT_EXCEPTION:
    case EnhanceSpeechTrackErrorType.UNSUPPORTED_FORMAT:
      return I18n.t("enhance_speech.unsupported_format");
    case EnhanceSpeechTrackErrorType.UNSUPPORTED_EXTENSION:
      return I18n.t("enhance_speech.unsupported_extension_or_content_type");
    case EnhanceSpeechTrackErrorType.INCALCULABLE_DURATION:
      return I18n.t("enhance_speech.incalculable_duration");
    case EnhanceSpeechTrackErrorType.FILE_SIZE_LIMIT:
      return I18n.t("enhance_speech.too_big", {
        fileSizeLimitDescription: userLimits.fileSizeLimitDescription,
      });
    case EnhanceSpeechTrackErrorType.RATE_LIMIT:
      return retryAtIntervalDescription
        ? I18n.t("enhance_speech.rate_limited.try_again_after", {
            retryInterval: retryAtIntervalDescription,
          })
        : I18n.t("enhance_speech.rate_limited.generic");
    case EnhanceSpeechTrackErrorType.UPLOAD_FAILED:
      return I18n.t("enhance_speech.upload_error");
    case EnhanceSpeechTrackErrorType.FILE_DURATION_LIMIT:
      return I18n.t("enhance_speech.too_long", {
        fileDurationLimitDescription: userLimits.fileDurationLimitDescription,
      });
    case EnhanceSpeechTrackErrorType.CUMULATIVE_DURATION_LIMIT:
      return I18n.t("enhance_speech.daily_duration_limit_exceeded", {
        dailyDurationLimitDescription: userLimits.dailyDurationLimitDescription,
      });
    case EnhanceSpeechTrackErrorType.COLLIGO_SILENT_INPUT:
      return I18n.t("enhance_speech.no_audio");
    default:
      return I18n.t("enhance_speech.generic_failure");
  }
};

export interface CommerceInfo {
  domain: string;
  url: string;
}

export const getIntervalDescription = (retryAfter?: string | null): string => {
  return I18n.distanceOfTimeInWords(new Date(), new Date(retryAfter ?? 0));
};

export const findEndOfCurrentRange = (
  currentTime: number,
  ranges: TimeRanges
): number => {
  const numRanges = ranges.length;

  if (
    numRanges === 0 ||
    ranges.start(0) > currentTime ||
    ranges.end(numRanges - 1) < currentTime
  ) {
    return 0;
  }

  let rangeIndex = 0;
  const maxRangeIndex = numRanges - 1;

  while (rangeIndex < maxRangeIndex && ranges.end(rangeIndex) < currentTime) {
    const nextRangeIndex = rangeIndex + 1;

    if (
      nextRangeIndex <= maxRangeIndex &&
      ranges.start(nextRangeIndex) <= currentTime
    ) {
      rangeIndex = nextRangeIndex;
    } else {
      break;
    }
  }

  return ranges.end(rangeIndex);
};

//output in minutes (tenths precision), or if > 60 minutes, in hours (tenths precision, unless whole value)
export const toPrintableTime = (minutes: number): string => {
  const minutesRemaining = Math.floor(minutes);
  const hours = minutesRemaining / 60;
  const hoursRemaining = Number.isInteger(hours)
    ? hours
    : parseFloat(hours.toFixed(1));
  return minutesRemaining < 60
    ? `${I18n.t("datetime.distance_in_words.x_minutes", {
        count: minutesRemaining,
      })}`
    : `${I18n.t("time.formats.x_hours", {
        count: hoursRemaining,
      })}`;
};

// 🐞 FOR DEBUGGING MEDIA ELEMENT BUFFERED/SEEKABLE/PLAYED TIME RANGES
export const timeRangesToString = (
  ranges: TimeRanges | null | undefined
): string => {
  if (!ranges) {
    return "[]";
  }
  let timeRangesString = "";
  for (let timeRangeIdx = 0; timeRangeIdx < ranges.length; timeRangeIdx++) {
    timeRangesString += `[${ranges.start(timeRangeIdx)}, ${ranges.end(
      timeRangeIdx
    )}]\n`;
  }
  return timeRangesString;
};

export const getEnhanceProcessingState = (
  track: EnhanceSpeechTrack
): ProcessingState => {
  if (!!track.finishedProcessingAt || track.errorType) {
    return ProcessingState.finished;
  }

  // this needs to be checked before startedProcessingAt
  if (!track.originalAudioUrl) {
    return ProcessingState.uploading;
  }

  if (track.limits?.isLimited) {
    return ProcessingState.paused;
  }

  if (!track.startedProcessingAt) {
    return ProcessingState.queued;
  }

  if (!track.finishedProcessingAt) {
    return ProcessingState.processing;
  }

  return ProcessingState.finished;
};
